import { isWithin24Hours } from '@/features/customers/utils/timeValidation';
import type { SubscriptionAction } from '@/features/subscriptions';
import {
  SubscriptionInfoService,
  useSubscriptionActions,
  WARNING_FRESHNESS_WINDOW_MS,
} from '@/features/subscriptions';
import { useState } from 'react';

export const useCustomerActions = (onSuccess?: () => void) => {
  const {
    executeAction,
    isLoading: isActionLoading,
    error: actionError,
    clearError,
  } = useSubscriptionActions(onSuccess);

  // Modal state management
  const [confirmAction, setConfirmAction] = useState<{
    action: SubscriptionAction;
    title: string;
    message: string;
    confirmText: string;
    confirmColor: 'danger' | 'warning' | 'primary' | 'success';
  } | null>(null);
  const [reasonModalAction, setReasonModalAction] = useState<'cancel' | 'pause' | null>(null);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showShipNowWarning, setShowShipNowWarning] = useState(false);
  const [showChangeDateWarning, setShowChangeDateWarning] = useState(false);
  const [pendingShipNowCustomerId, setPendingShipNowCustomerId] = useState<number | null>(null);
  const [pendingChangeDateCustomerId, setPendingChangeDateCustomerId] = useState<number | null>(
    null,
  );
  const [pendingChangeDate, setPendingChangeDate] = useState<string | null>(null);
  const [lastOrderPlacedAt, setLastOrderPlacedAt] = useState<string | null>(null);
  const [lastOrderFetchedAt, setLastOrderFetchedAt] = useState<number | null>(null);

  // Success message state
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Action handlers
  const handleAction = async (action: SubscriptionAction, shopifyCustomerId?: number) => {
    if (!shopifyCustomerId) return;

    clearError();

    // For cancel and pause, show reason modal first
    if (action === 'cancel' || action === 'pause') {
      setReasonModalAction(action);
      return;
    }

    // For changeDispatchDate, fetch last order date and maybe show warning
    if (action === 'changeDispatchDate') {
      setPendingChangeDateCustomerId(shopifyCustomerId);
      setPendingChangeDate(null);
      const { lastOrderPlacedAt: last } =
        await SubscriptionInfoService.getLastOrderDate(shopifyCustomerId);
      setLastOrderPlacedAt(last);
      setLastOrderFetchedAt(Date.now());
      if (isWithin24Hours(last)) {
        setShowChangeDateWarning(true);
        return;
      }
      setShowDateModal(true);
      return;
    }

    // For shipNow, fetch last order date and maybe show warning
    if (action === 'shipNow') {
      const { lastOrderPlacedAt: last } =
        await SubscriptionInfoService.getLastOrderDate(shopifyCustomerId);
      setLastOrderPlacedAt(last);
      setLastOrderFetchedAt(Date.now());
      if (isWithin24Hours(last)) {
        setPendingShipNowCustomerId(shopifyCustomerId);
        setShowShipNowWarning(true);
        return;
      }
    }

    // For other actions, show confirmation modal
    let actionConfig = null;

    switch (action) {
      case 'reactivate':
        actionConfig = {
          action: 'reactivate' as SubscriptionAction,
          title: 'Reactivate Subscription',
          message: 'This subscription will be reactivated and billing will resume.',
          confirmText: 'Reactivate Subscription',
          confirmColor: 'primary' as const,
        };
        break;
      case 'shipNow':
        actionConfig = {
          action: 'shipNow' as SubscriptionAction,
          title: 'Ship Now',
          message: 'This will trigger an immediate order for this subscription.',
          confirmText: 'Ship Now',
          confirmColor: 'success' as const,
        };
        break;
    }

    setConfirmAction(actionConfig);
  };

  const handleConfirmAction = async (shopifyCustomerId: number) => {
    if (confirmAction) {
      const success = await executeAction(shopifyCustomerId, confirmAction.action);
      setConfirmAction(null);

      // Show success message only if action was successful
      if (success) {
        const successMessages: Record<string, string> = {
          reactivate: 'Subscription is re-activated',
          shipNow: 'Order is under processing',
        };
        setSuccessMessage(successMessages[confirmAction.action] || 'Action completed successfully');
      }
    }
  };

  const handleReasonConfirm = async (
    shopifyCustomerId: number,
    reason: string,
    comment: string,
  ) => {
    if (reasonModalAction) {
      const success = await executeAction(shopifyCustomerId, reasonModalAction, {
        reasonId: reason,
        comment,
      });
      setReasonModalAction(null);

      // Show success message only if action was successful
      if (success) {
        const successMessages: Record<string, string> = {
          cancel: 'Subscription is cancelled',
          pause: 'Subscription is paused',
        };
        setSuccessMessage(successMessages[reasonModalAction] || 'Action completed successfully');
      }
    }
  };

  const handleDateConfirm = async (shopifyCustomerId: number, date: string) => {
    // Freshness check: if stale (> threshold), refetch and compare
    const isFresh =
      lastOrderFetchedAt !== null && Date.now() - lastOrderFetchedAt <= WARNING_FRESHNESS_WINDOW_MS;
    const effectiveLastOrder = lastOrderPlacedAt;

    if (!isFresh) {
      const { lastOrderPlacedAt: latest } =
        await SubscriptionInfoService.getLastOrderDate(shopifyCustomerId);
      if (latest !== effectiveLastOrder) {
        setLastOrderPlacedAt(latest);
        setLastOrderFetchedAt(Date.now());
        // Close DateModal before showing warning to avoid overlapping modals
        setShowDateModal(false);
        setPendingChangeDate(date);
        setShowChangeDateWarning(true);
        return;
      }
      setLastOrderPlacedAt(latest);
      setLastOrderFetchedAt(Date.now());
    }

    const success = await executeAction(shopifyCustomerId, 'changeDispatchDate', {
      nextDispatchDate: date,
    });
    setShowDateModal(false);

    if (success) {
      setSuccessMessage('Dispatch Date is changed');
    }
  };

  const clearSuccessMessage = () => {
    setSuccessMessage(null);
  };

  const showPageSuccess = (message: string) => {
    setSuccessMessage(message);
  };

  // Warning modal handlers
  const handleWarningYes = async () => {
    if (showShipNowWarning && pendingShipNowCustomerId) {
      const success = await executeAction(pendingShipNowCustomerId, 'shipNow');
      setShowShipNowWarning(false);
      setPendingShipNowCustomerId(null);

      // Show success message only if action was successful
      if (success) {
        setSuccessMessage('Order is under processing');
      }
      return;
    }
    if (showChangeDateWarning && pendingChangeDateCustomerId) {
      if (pendingChangeDate) {
        // Second-step confirm: execute action with stored date
        const success = await executeAction(pendingChangeDateCustomerId, 'changeDispatchDate', {
          nextDispatchDate: pendingChangeDate,
        });
        setShowChangeDateWarning(false);
        setPendingChangeDateCustomerId(null);
        setPendingChangeDate(null);
        if (success) {
          setSuccessMessage('Dispatch Date is changed');
        }
      } else {
        // First-step confirm: open the date modal to pick a date
        setShowChangeDateWarning(false);
        setShowDateModal(true);
      }
    }
  };

  const handleWarningNo = () => {
    setShowShipNowWarning(false);
    setPendingShipNowCustomerId(null);
    setShowChangeDateWarning(false);
    setPendingChangeDateCustomerId(null);
    setPendingChangeDate(null);
  };

  return {
    // State
    confirmAction,
    reasonModalAction,
    showDateModal,
    showShipNowWarning,
    showChangeDateWarning,
    actionError,
    successMessage,

    // Functions
    handleAction,
    handleConfirmAction,
    handleReasonConfirm,
    handleDateConfirm,
    handleWarningYes,
    handleWarningNo,
    clearError,
    clearSuccessMessage,
    showPageSuccess,
    isActionLoading,

    // Modal controls
    setConfirmAction,
    setReasonModalAction,
    setShowDateModal,
  };
};
